/**
 * Auth0 Provider for Admin UI
 * Wraps the app in Auth0Provider and registers token service
 */

import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { ReactNode, useEffect } from 'react';
import { tokenService } from './token-service';

const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN;
const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID;
const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE;

if (!AUTH0_DOMAIN || !AUTH0_CLIENT_ID || !AUTH0_AUDIENCE) {
  throw new Error(
    '[AuthProvider] Missing required Auth0 environment variables: ' +
      'VITE_AUTH0_DOMAIN, VITE_AUTH0_CLIENT_ID, VITE_AUTH0_AUDIENCE must all be set.'
  );
}

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Inner component — lives inside Auth0Provider so it has access to the
 * Auth0 context via useAuth0(). Registers getAccessTokenSilently with
 * tokenService so the API client can get tokens without being a hook.
 */
function TokenServiceRegistrar({ children }: { children: ReactNode }) {
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    tokenService.register(getAccessTokenSilently);
    return () => tokenService.clear();
  }, [getAccessTokenSilently]);

  return <>{children}</>;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const redirectUri = `${window.location.origin}/callback`;

  console.log('[AuthProvider] Config:', {
    domain: AUTH0_DOMAIN,
    clientId: AUTH0_CLIENT_ID,
    audience: AUTH0_AUDIENCE,
    redirectUri,
  });

  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: redirectUri,
        audience: AUTH0_AUDIENCE,
        scope: 'openid profile email',
      }}
      cacheLocation="localstorage"
      useRefreshTokens
      useRefreshTokensFallback
    >
      <TokenServiceRegistrar>{children}</TokenServiceRegistrar>
    </Auth0Provider>
  );
}
